/**
 * useAccountSettings — the preferences behind Account Settings.
 *
 * Loads GET /UserProfile/GetAccountSettings, tracks the unsaved draft and
 * pushes the batch back through POST /UserProfile/UpdateAccountSettings,
 * mirroring the result onto the cached profile.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import profileService from '../api/services/profileService';
import { useProfile } from '../context/ProfileContext';
import {
  PROFILE_VISIBILITY_LABEL,
  WEIGHT_VISIBILITY_LABEL,
  profileVisibilityKeyFromId,
  weightVisibilityKeyFromId,
} from '../constants/accountSettings';
import { getApiErrorMessage } from '../utils/apiErrorHandler';
import { WEIGHT_SCALE_UNIT_ID } from '../utils/weightUtils';
import type { ApiError } from '../types/api';
import type {
  AccountSettingsResponse,
  AccountSettingsUpdateRequest,
} from '../types/userProfile';

/** The fields the update endpoint accepts, in the order it expects them. */
const EDITABLE_FIELDS = [
  'weightScaleUnit',
  'profileViewPrivacy',
  'weightPrivacy',
  'isOnlineVisible',
  'notificationsEnabled',
  'videosStartWithSound',
  'allowSharing',
] as const;

type EditableField = (typeof EDITABLE_FIELDS)[number];

export interface AccountSettingsSaveResult {
  success: boolean;
  message?: string;
}

export interface UseAccountSettingsResult {
  settings: AccountSettingsResponse | null;
  loading: boolean;
  saving: boolean;
  error: string;
  hasChanges: boolean;
  setField: <K extends EditableField>(
    field: K,
    value: AccountSettingsResponse[K],
  ) => void;
  reload: () => void;
  save: () => Promise<AccountSettingsSaveResult>;
}

const buildPayload = (
  settings: AccountSettingsResponse,
): AccountSettingsUpdateRequest => ({
  weightScaleUnit: settings.weightScaleUnit ?? WEIGHT_SCALE_UNIT_ID.KG,
  profileViewPrivacy: settings.profileViewPrivacy,
  weightPrivacy: settings.weightPrivacy,
  isOnlineVisible: settings.isOnlineVisible,
  notificationsEnabled: settings.notificationsEnabled,
  videosStartWithSound: settings.videosStartWithSound,
  allowSharing: settings.allowSharing,
});

export const useAccountSettings = (): UseAccountSettingsResult => {
  const { updateProfileLocal } = useProfile();
  const [settings, setSettings] = useState<AccountSettingsResponse | null>(null);
  const [savedSettings, setSavedSettings] = useState<AccountSettingsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await profileService.getAccountSettings();
      const body = response?.data;
      if (body?.isSuccess === false || !body?.data) {
        setError(body?.message || '');
        return;
      }
      setSettings(body.data);
      setSavedSettings(body.data);
    } catch (caught) {
      setError(getApiErrorMessage(caught as ApiError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load().catch(() => undefined);
  }, [load, reloadToken]);

  const setField = useCallback(
    <K extends EditableField>(field: K, value: AccountSettingsResponse[K]) => {
      setSettings(previous => (previous ? { ...previous, [field]: value } : previous));
    },
    [],
  );

  const hasChanges = useMemo(() => {
    if (!settings || !savedSettings) return false;
    return EDITABLE_FIELDS.some(field => settings[field] !== savedSettings[field]);
  }, [savedSettings, settings]);

  const save = useCallback(async (): Promise<AccountSettingsSaveResult> => {
    if (!settings) return { success: false };
    setSaving(true);
    try {
      const response = await profileService.updateAccountSettings(buildPayload(settings));
      const body = response?.data;
      if (body?.isSuccess === false) {
        return { success: false, message: body?.message || undefined };
      }

      const updated = body?.data ?? settings;
      setSettings(updated);
      setSavedSettings(updated);

      // Keep the cached profile in step so the rest of the app sees the change.
      await updateProfileLocal({
        unit: updated.weightScaleUnit === WEIGHT_SCALE_UNIT_ID.LBS ? 'lbs' : 'kg',
        profileViewPrivacy:
          PROFILE_VISIBILITY_LABEL[profileVisibilityKeyFromId(updated.profileViewPrivacy)],
        weightPrivacy:
          WEIGHT_VISIBILITY_LABEL[weightVisibilityKeyFromId(updated.weightPrivacy)],
        onlineVisibility: updated.isOnlineVisible,
        notificationsEnabled: updated.notificationsEnabled,
        videosSoundEnabled: updated.videosStartWithSound,
        allowSharing: updated.allowSharing,
      });

      return { success: true };
    } catch (caught) {
      return { success: false, message: getApiErrorMessage(caught as ApiError) };
    } finally {
      setSaving(false);
    }
  }, [settings, updateProfileLocal]);

  const reload = useCallback(() => setReloadToken(token => token + 1), []);

  return { settings, loading, saving, error, hasChanges, setField, reload, save };
};

export default useAccountSettings;
